/*
 * Person area, grants half (audit Phase 3.3): the Properties, Applications
 * and Permissions tabs of the Person Detail modal. Replaces the 581-line
 * wwwroot/js/Person/Claim.js.
 *
 * Three tables, one shape (see areas/person-common.ts):
 *   Properties   PersonClaim rows with ResourceId = null -- a name/value pair
 *                carried in the person's token.
 *   Applications a person's access to a whole Application, added and removed
 *                through ApplicationPerson rather than PersonClaim.
 *   Permissions  PersonClaim rows against a Resource, with a value type and
 *                an optional ResourcePolicy time window.
 *
 * The create/edit endpoints for Properties and Permissions answer HTML, not
 * the JSON envelope: a saved row renders as the read-only row, a rejected one
 * re-renders the form plus a *-row-validation sibling. The response is told
 * apart by the validation class, which is why these two go through ajaxHtml.
 *
 * Defects fixed in the move:
 *  - ResourceEdit.cshtml called ApplicationFilterChanged(this), passing the
 *    <select>. `claimId == 0` was then false, so the row selector became
 *    tr.person-resource-row[data-id='[object HTMLSelectElement]'] and matched
 *    nothing: changing the Application of an existing permission silently
 *    failed to reload the Resource list. It passes the claim id now.
 *  - PropertyClaimTypeChange / ResourceValueTypeChange re-bound a jQuery UI
 *    autocomplete by hand after the swap; core/enhance turns a data-autocomp
 *    control into a datalist automatically, so the hand-rolled branch (which
 *    read .data('autocomp') off a control that had just been replaced, i.e.
 *    off the detached node) is gone.
 *  - ApplicationFilterChanged disabled .fa-edit/.fa-trash -- icons that do not
 *    exist on an entry row -- and re-enabled row.find('fa-save'), a selector
 *    for an <fa-save> element. Neither did anything.
 *  - The entry rows collapsed to colspan=3 in tables of 2, 5 and 7 columns.
 */
import { ajaxHtml, ajaxJson, ajaxSwap } from '../core/ajax';
import { one, swapHtml } from '../core/dom';
import { confirm } from '../core/modal';
import { toast } from '../core/toast';
import { setValidationModelState } from '../core/validation';
import {
    checkedIn, currentDomainId, personId, remove, resetEntryRow,
    selectedOptionIn, setDisabled, setHidden, summaryOf, valueIn,
} from './person-common';

const APP_ROW = 'tr.new-person-application-row';
const PROPERTY_ROW = 'tr.new-person-property-row';
const RESOURCE_ROW = 'tr.new-person-resource-row';

/** A row selector for an id, or the entry row when the id is 0. */
function rowFor(kind: string, claimId: number | string): string {
    return String(claimId) === '0' ? `tr.new-person-${kind}-row` : `tr.person-${kind}-row[data-id='${claimId}']`;
}

/**
 * POST/PUT an entry-row form whose response is HTML: the saved row on
 * success, the form again plus a validation sibling on rejection.
 */
async function submitEntryRow(
    url: string, data: Record<string, unknown>, rowSelector: string,
    validationClass: string, reset: () => void, errorTitle: string,
): Promise<void> {
    const row = one(rowSelector);
    if (!row) return;
    remove(`tr.${validationClass}[data-id='0']`);
    setDisabled(`${rowSelector} .fa-save, ${rowSelector} .fa-undo`, true);
    setHidden(`${rowSelector} .fa-spinner`, false);
    const html = await ajaxHtml({
        url,
        method: 'POST',
        data,
        onError: (_xhr, _status, error) => { toast('danger', errorTitle, error); return true; },
        onComplete: () => {
            setDisabled(`${rowSelector} .fa-save, ${rowSelector} .fa-undo`, false);
            setHidden(`${rowSelector} .fa-spinner`, true);
        },
    });
    if (html === null) return;
    if (html.includes(validationClass)) {
        swapHtml(row, html, 'replaceWith');     // still the form: validation failed
    } else {
        swapHtml(row, html, 'before');
        reset();
    }
}

// ---------------------------------------------------------------- Applications tab

export const Claim = {
    AddApplicationAssign(): void {
        setHidden(APP_ROW, false);
        void ajaxSwap({
            container: APP_ROW,
            url: `PersonClaim/Person/${personId()}/Create/Application?domainId=${encodeURIComponent(currentDomainId())}`,
            mode: 'replaceWith',
            busy: `${APP_ROW} .fa-spinner`,
            alertTitle: 'Failed to initialize the new application entry-form',
        });
    },

    SaveApplicationAssign(): void {
        const person = personId();
        const appId = valueIn(one(APP_ROW), '#ApplicationId');
        if (!(Number(person) > 0) || !(Number(appId) > 0)) {
            toast('warning', 'Pick an application first');
            return;
        }
        setDisabled(`${APP_ROW} .fa-save, ${APP_ROW} .fa-undo`, true);
        setHidden(`${APP_ROW} .fa-spinner`, false);
        void ajaxJson({
            url: `ApplicationPerson/${appId}/Person/${person}`,
            method: 'POST',
            onSuccess: () => {
                // No per-(person, application) row-render endpoint exists, so
                // the whole tab comes back and the new assignment lands in the
                // server's sort order with the rest.
                void ajaxSwap({
                    container: 'PersonApplicationsTab',
                    url: `PersonClaim/Person/${person}/Applications`,
                    alertTitle: 'Failed to refresh the Applications tab',
                });
                toast('success', 'Application access granted');
            },
            onFailure: data => { toast('danger', 'Failed to assign the application', summaryOf(data)); return true; },
            onError: (_xhr, _status, error) => { toast('danger', 'Failed to assign the application', error); return true; },
            onComplete: () => {
                setDisabled(`${APP_ROW} .fa-save, ${APP_ROW} .fa-undo`, false);
                setHidden(`${APP_ROW} .fa-spinner`, true);
            },
        });
    },

    CancelApplicationAssign(): void {
        resetEntryRow('new-person-application-row', 'person-application-row-validation', 2);
    },

    RemoveApplicationAssign(appId: number | string): void {
        const rowSelector = `tr.person-application-row[data-id='${appId}']`;
        const name = one(`${rowSelector} td`)?.textContent?.trim() ?? '';
        void confirm({
            title: 'Remove application access',
            message: name
                ? `Remove all access to "${name}"? Every permission this person holds on it is revoked.`
                : 'Remove all access to this application?',
            confirmText: 'Remove',
            variant: 'danger',
            onConfirm: () => {
                setDisabled(`${rowSelector} .fa-trash`, true);
                setHidden(`${rowSelector} .fa-spinner`, false);
                void ajaxJson({
                    url: `ApplicationPerson/${appId}/Person/${personId()}`,
                    method: 'DELETE',
                    onSuccess: () => { remove(rowSelector); toast('success', 'Application access removed'); },
                    onFailure: data => { toast('danger', 'Failed to remove the application', summaryOf(data)); return true; },
                    onError: (_xhr, _status, error) => { toast('danger', 'Failed to remove the application', error); return true; },
                    onComplete: () => {
                        setDisabled(`${rowSelector} .fa-trash`, false);
                        setHidden(`${rowSelector} .fa-spinner`, true);
                    },
                });
            },
        });
    },

    // ------------------------------------------------------------ Properties tab

    CreatePropertyClaim(): void {
        setHidden(PROPERTY_ROW, false);
        void ajaxSwap({
            container: PROPERTY_ROW,
            url: `PersonClaim/Person/${personId()}/Create/Property?domainId=${encodeURIComponent(currentDomainId())}`,
            mode: 'replaceWith',
            busy: `${PROPERTY_ROW} .fa-spinner`,
            alertTitle: 'Failed to initialize the new property entry-form',
        });
    },

    PostPropertyClaim(): void {
        const row = one(PROPERTY_ROW);
        const type = selectedOptionIn(row, '#Claim_ClaimType');
        void submitEntryRow('PersonClaim/Property', {
            Id: 0,
            PersonId: valueIn(row, '#Claim_PersonId'),
            ClaimType: valueIn(row, '#Claim_ClaimType'),
            ClaimTypeId: type?.getAttribute('data-id') ?? '',
            ClaimValue: valueIn(row, '#Claim_ClaimValue'),
            ClaimValueType: type?.getAttribute('data-valuetype') ?? '',
            IsDeny: checkedIn(row, '#Claim_IsDeny'),
            ResourcePolicyId: valueIn(row, '#Claim_ResourcePolicyId'),
        }, PROPERTY_ROW, 'person-property-row-validation', Claim.CancelPropertyCreate, 'Failed to save the property');
    },

    EditPropertyClaim(claimId: number | string): void {
        const rowSelector = `tr.person-property-row[data-id='${claimId}']`;
        setDisabled(`${rowSelector} .fa-edit, ${rowSelector} .fa-trash`, true);
        void ajaxSwap({
            container: rowSelector,
            url: `PersonClaim/Edit/Property/${claimId}?domainId=${encodeURIComponent(currentDomainId())}`,
            mode: 'replaceWith',
            busy: `${rowSelector} .fa-spinner`,
            alertTitle: 'Failed to load the property to edit',
            // The row is replaced wholesale, so the icons restore themselves.
        });
    },

    SavePropertyClaim(claimId: number | string): void {
        const rowSelector = `tr.person-property-row[data-id='${claimId}']`;
        const row = one(rowSelector);
        const type = selectedOptionIn(row, '#Claim_ClaimType');
        remove(`tr.person-property-row-validation[data-id='${claimId}']`);
        setDisabled(`${rowSelector} .fa-save, ${rowSelector} .fa-undo`, true);
        void ajaxSwap({
            container: rowSelector,
            url: 'PersonClaim/Property',
            method: 'PUT',
            data: {
                Id: claimId,
                PersonId: valueIn(row, '#Claim_PersonId'),
                ClaimType: valueIn(row, '#Claim_ClaimType'),
                ClaimValue: valueIn(row, '#Claim_ClaimValue'),
                ClaimValueType: type?.getAttribute('data-valuetype') ?? '',
                IsDeny: checkedIn(row, '#Claim_IsDeny'),
                ResourcePolicyId: valueIn(row, '#Claim_ResourcePolicyId'),
            },
            mode: 'replaceWith',
            busy: `${rowSelector} .fa-spinner`,
            onError: (_xhr, _status, error) => {
                toast('danger', 'Failed to save the property changes', error);
                setDisabled(`${rowSelector} .fa-save, ${rowSelector} .fa-undo`, false);
                return true;
            },
        });
    },

    CancelPropertyCreate(): void {
        resetEntryRow('new-person-property-row', 'person-property-row-validation', 5);
    },

    CancelPropertyEdit(claimId: number | string): void {
        const rowSelector = `tr.person-property-row[data-id='${claimId}']`;
        remove(`tr.person-property-row-validation[data-id='${claimId}']`);
        setDisabled(`${rowSelector} .fa-edit, ${rowSelector} .fa-trash`, true);
        void ajaxSwap({
            container: rowSelector,
            url: `PersonClaim/Property/${claimId}`,
            mode: 'replaceWith',
            busy: `${rowSelector} .fa-spinner`,
            alertTitle: 'Failed to display the property',
            onComplete: () => setDisabled(`${rowSelector} .fa-edit, ${rowSelector} .fa-trash`, false),
        });
    },

    RemovePropertyClaim(claimId: number | string): void {
        const rowSelector = `tr.person-property-row[data-id='${claimId}']`;
        void confirm({
            title: 'Remove property',
            message: 'Remove this property?',
            confirmText: 'Remove',
            variant: 'danger',
            onConfirm: () => {
                setDisabled(`${rowSelector} .fa-trash`, true);
                setHidden(`${rowSelector} .fa-spinner`, false);
                void ajaxJson({
                    url: `PersonClaim/${claimId}`,
                    method: 'DELETE',
                    onSuccess: () => { remove(rowSelector); toast('success', 'Property removed'); },
                    onFailure: data => { toast('danger', 'Failed to remove the property', summaryOf(data)); return true; },
                    onError: (_xhr, _status, error) => { toast('danger', 'Failed to remove the property', error); return true; },
                    onComplete: () => {
                        setDisabled(`${rowSelector} .fa-trash`, false);
                        setHidden(`${rowSelector} .fa-spinner`, true);
                    },
                });
            },
        });
    },

    /** The chosen ClaimType decides the value control; the server renders it. */
    PropertyClaimTypeChange(claimId: number | string): void {
        const rowSelector = rowFor('property', claimId);
        const row = one(rowSelector);
        const type = selectedOptionIn(row, '#Claim_ClaimType');
        remove(`tr.person-property-row-validation[data-id='${claimId}']`);
        setDisabled(`${rowSelector} .fa-edit, ${rowSelector} .fa-trash`, true);
        void ajaxSwap({
            container: `${rowSelector} #Claim_ClaimValue`,
            url: `PersonClaim/ControlHtml?domainId=${encodeURIComponent(currentDomainId())}`,
            method: 'POST',
            data: {
                Id: claimId,
                PersonId: valueIn(row, '#Claim_PersonId'),
                ClaimType: valueIn(row, '#Claim_ClaimType'),
                ClaimValue: valueIn(row, '#Claim_ClaimValue'),
                ClaimValueType: type?.getAttribute('data-valuetype') ?? '',
            },
            mode: 'replaceWith',
            busy: `${rowSelector} .fa-spinner`,
            // A data-autocomp control becomes a datalist through the swap enhancers.
            onError: (_xhr, _status, error) => {
                toast('danger', 'Failed to load the value control', error);
                return true;
            },
            onComplete: () => setDisabled(`${rowSelector} .fa-edit, ${rowSelector} .fa-trash`, false),
        });
    },

    // ------------------------------------------------------------ Permissions tab

    CreateResourceClaim(): void {
        setHidden(RESOURCE_ROW, false);
        void ajaxSwap({
            container: RESOURCE_ROW,
            url: `PersonClaim/Person/${personId()}/Create/Resource?domainId=${encodeURIComponent(currentDomainId())}`,
            mode: 'replaceWith',
            busy: `${RESOURCE_ROW} .fa-spinner`,
            alertTitle: 'Failed to initialize the new permission entry-form',
        });
    },

    PostResourceClaim(): void {
        const row = one(RESOURCE_ROW);
        // domainId rides in the QUERY string, not the form: a form key named
        // domainId would also bind PersonClaimModel.DomainId, which the Api
        // validates the resource and claim value against. The failure path
        // needs it to rebuild the application / resource / value-type pickers.
        void submitEntryRow(
            `PersonClaim/Resource?domainId=${encodeURIComponent(currentDomainId())}`, {
                Id: 0,
                PersonId: valueIn(row, '#PersonId'),
                SelectedApplication: valueIn(row, '#SelectedApplication'),
                ResourceId: valueIn(row, '#Claim_ResourceId'),
                ClaimValue: valueIn(row, '#Claim_ClaimValue'),
                ClaimValueType: valueIn(row, '#Claim_ClaimValueType'),
                IsDeny: checkedIn(row, '#Claim_IsDeny'),
                ResourcePolicyId: valueIn(row, '#Claim_ResourcePolicyId'),
            }, RESOURCE_ROW, 'person-resource-row-validation', Claim.CancelResourceClaimCreate,
            'Failed to save the permission');
    },

    EditResourceClaim(claimId: number | string): void {
        const rowSelector = `tr.person-resource-row[data-id='${claimId}']`;
        setDisabled(`${rowSelector} .fa-edit, ${rowSelector} .fa-trash`, true);
        void ajaxSwap({
            container: rowSelector,
            url: `PersonClaim/Edit/Resource/${claimId}`,
            mode: 'replaceWith',
            busy: `${rowSelector} .fa-spinner`,
            alertTitle: 'Failed to load the permission to edit',
        });
    },

    SaveResourceClaim(claimId: number | string): void {
        const rowSelector = `tr.person-resource-row[data-id='${claimId}']`;
        const row = one(rowSelector);
        remove(`tr.person-resource-row-validation[data-id='${claimId}']`);
        setDisabled(`${rowSelector} .fa-save, ${rowSelector} .fa-undo`, true);
        setHidden(`${rowSelector} .fa-spinner`, false);
        void ajaxJson({
            url: 'PersonClaim/Resource',
            method: 'PUT',
            data: {
                Id: claimId,
                PersonId: valueIn(row, '#PersonId'),
                ResourceId: valueIn(row, '#Claim_ResourceId'),
                ClaimValue: valueIn(row, '#Claim_ClaimValue'),
                ClaimValueType: valueIn(row, '#Claim_ClaimValueType'),
                IsDeny: checkedIn(row, '#Claim_IsDeny'),
                // Empty string binds to null on an int? -- that is how
                // "(always)" clears a window. The key must always be present:
                // PersonClaimController treats an ABSENT key as "leave it alone".
                ResourcePolicyId: valueIn(row, '#Claim_ResourcePolicyId'),
            },
            onSuccess: () => Claim.CancelResourceClaimEdit(claimId),
            onFailure: data => {
                const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
                if (!modelState) { toast('danger', 'Failed to save the permission changes'); return true; }
                // The server keys these on the flat names; the row's validation
                // spans are keyed on the bound paths.
                setValidationModelState({
                    ...modelState,
                    ...(modelState['ClaimValue'] ? { 'Claim.ClaimValue': modelState['ClaimValue'] } : {}),
                    ...(modelState['ClaimValueType'] ? { 'Claim.ClaimValueType': modelState['ClaimValueType'] } : {}),
                }, rowSelector);
                return true;
            },
            onError: (_xhr, _status, error) => { toast('danger', 'Failed to save the permission changes', error); return true; },
            onComplete: () => {
                setDisabled(`${rowSelector} .fa-save, ${rowSelector} .fa-undo`, false);
                setHidden(`${rowSelector} .fa-spinner`, true);
            },
        });
    },

    CancelResourceClaimCreate(): void {
        resetEntryRow('new-person-resource-row', 'person-resource-row-validation', 7);
    },

    CancelResourceClaimEdit(claimId: number | string): void {
        const rowSelector = `tr.person-resource-row[data-id='${claimId}']`;
        remove(`tr.person-resource-row-validation[data-id='${claimId}']`);
        setDisabled(`${rowSelector} .fa-edit, ${rowSelector} .fa-trash`, true);
        void ajaxSwap({
            container: rowSelector,
            url: `PersonClaim/Resource/${claimId}`,
            mode: 'replaceWith',
            busy: `${rowSelector} .fa-spinner`,
            alertTitle: 'Failed to display the permission',
            onComplete: () => setDisabled(`${rowSelector} .fa-edit, ${rowSelector} .fa-trash`, false),
        });
    },

    RemoveResourceClaim(claimId: number | string): void {
        const rowSelector = `tr.person-resource-row[data-id='${claimId}']`;
        void confirm({
            title: 'Remove permission',
            message: 'Remove this resource permission?',
            confirmText: 'Remove',
            variant: 'danger',
            onConfirm: () => {
                setDisabled(`${rowSelector} .fa-trash`, true);
                setHidden(`${rowSelector} .fa-spinner`, false);
                void ajaxJson({
                    url: `PersonClaim/${claimId}`,
                    method: 'DELETE',
                    onSuccess: () => { remove(rowSelector); toast('success', 'Permission removed'); },
                    onFailure: data => { toast('danger', 'Failed to remove the permission', summaryOf(data)); return true; },
                    onError: (_xhr, _status, error) => { toast('danger', 'Failed to remove the permission', error); return true; },
                    onComplete: () => {
                        setDisabled(`${rowSelector} .fa-trash`, false);
                        setHidden(`${rowSelector} .fa-spinner`, true);
                    },
                });
            },
        });
    },

    /** Application picker -> the Resource list it scopes. */
    ApplicationFilterChanged(claimId: number | string): void {
        const rowSelector = rowFor('resource', claimId);
        const row = one(rowSelector);
        const appId = valueIn(row, '#SelectedApplication') || '0';
        const resources = one(`${rowSelector} #Claim_ResourceId`) as HTMLSelectElement | null;
        remove(`tr.person-resource-row-validation[data-id='${claimId}']`);
        setDisabled(`${rowSelector} #Claim_ResourceId, ${rowSelector} .fa-save`, true);
        setHidden(`${rowSelector} .fa-spinner`, false);
        void ajaxJson({
            url: `Resource/Application/${appId}`,
            onSuccess: resp => {
                const list = ((resp as { data?: Array<{ resourceId: number; name: string; description?: string }> }).data) ?? [];
                // Value appId on the first option: a permission on the
                // application itself targets the application's own resource.
                resources?.replaceChildren(new Option('<Application>', appId));
                for (const resource of list) {
                    const option = new Option(resource.name, String(resource.resourceId));
                    if (resource.description) option.title = resource.description;
                    resources?.add(option);
                }
            },
            onFailure: data => {
                toast('danger', 'Failed to update the resource list after application change', summaryOf(data));
                return true;
            },
            onError: (_xhr, _status, error) => {
                toast('danger', 'Failed to update the resource list after application change', error);
                return true;
            },
            onComplete: () => {
                setDisabled(`${rowSelector} #Claim_ResourceId, ${rowSelector} .fa-save`, false);
                setHidden(`${rowSelector} .fa-spinner`, true);
            },
        });
    },

    /** The chosen value type decides the value control; the server renders it. */
    ResourceValueTypeChange(claimId: number | string): void {
        const rowSelector = rowFor('resource', claimId);
        const row = one(rowSelector);
        remove(`tr.person-resource-row-validation[data-id='${claimId}']`);
        setDisabled(`${rowSelector} .fa-save`, true);
        void ajaxSwap({
            container: `${rowSelector} #Claim_ClaimValue`,
            url: `PersonClaim/ControlHtml?domainId=${encodeURIComponent(currentDomainId())}`,
            method: 'POST',
            data: {
                Id: claimId,
                PersonId: valueIn(row, '#PersonId'),
                ResourceId: valueIn(row, '#Claim_ResourceId'),
                ClaimValue: valueIn(row, '#Claim_ClaimValue'),
                ClaimValueType: valueIn(row, '#Claim_ClaimValueType'),
            },
            mode: 'replaceWith',
            busy: `${rowSelector} .fa-spinner`,
            onError: (_xhr, _status, error) => {
                toast('danger', 'Failed to update the value edit control', error);
                return true;
            },
            onComplete: () => setDisabled(`${rowSelector} .fa-save`, false),
        });
    },
};
